/**
 * Full-res static image on demand — the web half of Unity's requestShotFullImage/setShotFullImage
 * pair (TimelineWebViewPanel.HandleRequestShotFullImageFromWeb).
 *
 * The slim DB the embedded app reads ships shots.staticimagedata as X'', so a static shot arrives
 * carrying only Unity's reduced cache thumbnail. That is the right pixel count for tiles; it is not
 * for the big viewer or fullscreen. Those ask here, and get back a `file://…{shotId}_full.jpg?v=N`
 * URL pointing at a byte-for-byte extraction of the blob. A file:// URL costs no JS heap (the
 * browser owns the decode, and jetsam reclaims it), which is the entire point of the round trip —
 * the alternative, base64 through EvaluateJavaScript, is 4-11MB of string per 12MP still.
 *
 * ── Where the result lives, and why it is NOT the project store ───────────────────────────────
 * A module-level cache, deliberately: a full-res URL written onto a shot would be rebroadcast by
 * every sceneReady merge and captured by every undo snapshot (up to MAX_UNDO of them), which is
 * the RAM shape this whole wave exists to remove. Consumers subscribe instead.
 *
 * ── The four contract notes (Unity review of cs:5682) ─────────────────────────────────────────
 * 1. Unity NEVER replies to a malformed request (shotId <= 0 / unparseable JSON), so a waiter for
 *    one is simply never fired. This callback API states that plainly rather than papering over it
 *    with a timeout: nothing here resolves on its own, and no consumer blocks on a reply (the cache
 *    thumbnail keeps painting until — and if — a URL arrives). The timeout lives in the promisified
 *    wrapper (`requestFullImageAsync`) instead, because a promise that never settles IS a leak; a
 *    callback that never fires is just a viewer that stays soft. Its window doubles as the expiry on
 *    the `pending` claim (`isClaimed`) — an unexpiring claim wedged the shot for the session.
 * 2. A non-null imageUrl does NOT mean the shot is currently displaying its static image — Unity
 *    does not consult isstaticimageenabled, it only checks hasstaticimage=1 and a non-empty blob.
 *    Consumers gate on shot state (useShotFullImage's hasStaticImage/videoObjectUrl checks), never
 *    on "a reply came back".
 * 3. The _full extractions are swept on webview teardown AND on the blank-screen RecreateWebView
 *    path, so a cached URL does not survive a reload. This cache is therefore session-scoped by
 *    construction: module state, no localStorage, no store, nothing that outlives the JS context.
 * 4. Replacing a shot's image (new drop, poster frame, AI apply) does NOT rewrite Unity's _full
 *    file — it is only written on request — so a cached URL would keep painting the OLD picture
 *    with stale ?v= ticks. There are two eviction hooks because there are two directions: Unity
 *    changing the blob (invalidateShotFullImage, called from features/timeline.ts's thumbnailChanged
 *    poke handler — evict and re-ask) and the web changing it optimistically before Unity has heard
 *    (dropShotFullImageForPendingWrite, called from store/actions.ts's five image mutators — evict
 *    WITHOUT re-asking; see its doc). clearShotFullImageCache() covers a non-refresh setSqlitePath,
 *    since shot ids are per-.previs and the next project's shot 11 must not be served this one's.
 */

import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';

// ─── Message types ───────────────────────────────────────────────────────

export interface SetShotFullImageMessage {
  type: 'setShotFullImage';
  /** `imageUrl` is bare JSON null on every absent/failure path (no blob, no open DB, no cache
   *  directory, or a throw) — never the string "null", never "". */
  payload: { shotId: number; requestId: string; imageUrl: string | null };
}

export type FullImageMessage = SetShotFullImageMessage;

// ─── Module state (session-scoped — see note 3) ──────────────────────────

/** Current shot + its immediate swipe neighbours. Small on purpose: each entry pins a decoded
 *  full-res image in the browser's cache, which is the resource this wave is budgeting. */
export const MAX_CACHED_FULL_IMAGES = 3;

/** One number for two jobs, and they have to be the same number: how long `requestFullImageAsync`
 *  waits before giving up, and how long a `pending` claim is believed. An ask that gave up IS an ask
 *  whose claim is dead, so the next one re-sends instead of waiting out a second full timeout on a
 *  request Unity was never going to answer. */
export const FULL_IMAGE_TIMEOUT_MS = 10_000;

/** shotId → file:// URL. Map iteration is insertion-ordered, so the first key IS the FIFO victim. */
const cache = new Map<number, string>();
/** shotId → live subscribers. Empty sets are deleted, so `waiters.has(id)` means "someone cares". */
const waiters = new Map<number, Set<(url: string | null) => void>>();
/** shotId → the ONE request currently outstanding, with the time it was sent. The id does the
 *  in-flight dedupe (two viewers showing the same shot share one round trip) and the stale-reply
 *  filter: a reply whose id is not the pending one belongs to a request that was invalidated or
 *  never made, and is dropped. The timestamp bounds how long the claim is believed — see
 *  `isClaimed`. */
const pending = new Map<number, { requestId: string; at: number }>();
let requestSeq = 0;

/**
 * Is a live request already covering this shot?
 *
 * A claim is the dedupe, so an unexpiring one is a wedge: note 1 says Unity never replies to a
 * request it cannot parse, and the handler's decode note says a decode that never settles leaves the
 * claim standing too. Either way the shot would stay un-requestable for the whole SESSION — every
 * later ask silently sending nothing. Past the window the claim is presumed dead and dropped here,
 * which both frees the caller to re-send and retires the old requestId, so a very late reply to it
 * is filtered out rather than resurrecting an entry nobody is waiting for.
 */
function isClaimed(shotId: number): boolean {
  const claim = pending.get(shotId);
  if (!claim) return false;
  if (Date.now() - claim.at < FULL_IMAGE_TIMEOUT_MS) return true;
  pending.delete(shotId);
  return false;
}

function notify(shotId: number, url: string | null): void {
  const set = waiters.get(shotId);
  if (!set) return;
  for (const cb of [...set]) cb(url);
}

function send(shotId: number): void {
  const requestId = `fullImage-${++requestSeq}-${shotId}`;
  pending.set(shotId, { requestId, at: Date.now() });
  // Payload fields are TOP-LEVEL, not nested: Unity's ShotFullImageRequestDto reads shotId and
  // requestId off the message root (JsonUtility.FromJson over the whole bridge message).
  enqueueSendToNative('requestShotFullImage', { shotId, requestId });
}

/**
 * Decode the new bitmap BEFORE any <img> src is swapped to it.
 *
 * React reuses the same <img> DOM node and only assigns a new `src`; per spec the element discards
 * its current image data on that assignment and paints nothing until the new one has decoded. A
 * multi-megapixel JPEG off file:// takes long enough on an iPad that the user would see
 * thumb → BLANK → sharp on every static shot, in the largest render target in the app — a worse
 * artifact than the softness this wave trades RAM for. Decoding into the browser's image cache
 * first means the swap lands on an already-decoded frame.
 *
 * Returns null when there is nothing to wait on — no Image constructor, no decode() support (jsdom,
 * older WebKit), or the call threw — and the caller then publishes synchronously. A decode
 * REJECTION also publishes: decode() failing is not proof the <img> cannot render it, and
 * withholding a URL Unity says exists would trade a slow paint for no paint at all.
 */
function startFullResDecode(url: string): Promise<unknown> | null {
  if (typeof Image === 'undefined') return null;
  try {
    const img = new Image();
    if (typeof img.decode !== 'function') return null;
    img.src = url;
    return img.decode();
  } catch {
    return null;
  }
}

/** Commit a decoded URL: re-checks that the request is still the live one (an invalidation during
 *  the decode retires it), then caches, evicts FIFO, and notifies. */
function publish(shotId: number, requestId: string, url: string): void {
  if (pending.get(shotId)?.requestId !== requestId) return;
  pending.delete(shotId);

  cache.set(shotId, url);
  while (cache.size > MAX_CACHED_FULL_IMAGES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
  notify(shotId, url);
}

/**
 * Drop what we hold for a shot and, if anyone is still watching it, reset them to null so they fall
 * back to the cache thumbnail. The reset is the load-bearing half: a consumer whose shot identity
 * did not change would otherwise keep rendering the stale URL forever.
 *
 * `reask` is the whole reason the two public wrappers exist, and getting it wrong is not a race but a
 * deterministic wrong answer. `send()` reaches the outbox synchronously, and the outbox is FIFO, so
 * a re-ask issued here is always the NEXT thing Unity handles — and Unity reads `staticimagedata` at
 * request time. Re-ask only when Unity's blob is already the fresh one.
 */
function drop(shotId: number, reask: boolean): void {
  cache.delete(shotId);
  pending.delete(shotId);
  if (!waiters.has(shotId)) return;
  notify(shotId, null);
  if (reask) send(shotId);
}

// ─── Public API ──────────────────────────────────────────────────────────

/**
 * Subscribe to the full-res URL for a shot, requesting it if it is not already cached or in flight.
 * The callback fires synchronously on a cache hit — a cached URL was decoded before it was cached,
 * so there is nothing to wait for — and on every later answer for that shot (a fresh URL, or null
 * when Unity has nothing / the entry was invalidated).
 *
 * Returns an unsubscribe. It does NOT cancel an in-flight request — the answer still populates the
 * cache, so swiping back to the shot costs no second round trip.
 */
export function requestFullImage(shotId: number, cb: (url: string | null) => void): () => void {
  let set = waiters.get(shotId);
  if (!set) { set = new Set(); waiters.set(shotId, set); }
  set.add(cb);

  const hit = cache.get(shotId);
  if (hit !== undefined) cb(hit);
  else if (!isClaimed(shotId)) send(shotId);

  return () => {
    const current = waiters.get(shotId);
    if (!current) return;
    current.delete(cb);
    if (current.size === 0) waiters.delete(shotId);
  };
}

/**
 * Cheap synchronous cache probe — does NOT subscribe, does NOT request. `useShotFullImage`'s scrub
 * debounce needs to know, before it starts a timer, whether this shot is free to serve: a cache hit
 * costs nothing (already decoded, no Unity round trip), so it has no business waiting out the same
 * window that exists to spare Unity a write for shots the user is only passing through.
 */
export function peekShotFullImageCache(shotId: number): string | undefined {
  return cache.get(shotId);
}

/**
 * One-shot, awaitable form of the above, for callers that need a VALUE rather than a subscription —
 * the AI orchestrators, which have to hand `resizeImageForUpload` a source before they can submit.
 *
 * Resolves NULL rather than rejecting or hanging on three paths that all mean the same thing to a
 * caller — Unity has nothing, the entry was invalidated mid-flight, or nobody answered within
 * `timeoutMs` — because they all have the same answer: carry on with the cache thumbnail. Note 1 is
 * why the timeout exists at all: Unity never replies to a malformed or unknown request, and a
 * promise that never settles is a leak (a callback that never fires is not — it just leaves the
 * viewer soft). A generation that degrades in resolution beats one that never starts.
 *
 * The subscription is dropped on every exit, including the timeout, so a caller that gave up is not
 * still holding the shot's waiter set open. Note the cache hit fires the callback SYNCHRONOUSLY,
 * before `requestFullImage` has returned its unsubscribe — hence the trailing re-check.
 */
export function requestFullImageAsync(
  shotId: number,
  timeoutMs: number = FULL_IMAGE_TIMEOUT_MS,
): Promise<string | null> {
  return new Promise(resolve => {
    let done = false;
    let unsubscribe: (() => void) | null = null;

    const settle = (url: string | null): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsubscribe?.();
      resolve(url);
    };

    const timer = setTimeout(() => settle(null), timeoutMs);
    unsubscribe = requestFullImage(shotId, settle);
    if (done) unsubscribe();          // cache hit: settle ran before `unsubscribe` existed
  });
}

/**
 * Contract note 4, UNITY side: the shot's blob changed under us and Unity already holds the new
 * pixels — evict and go get them. Called from the `thumbnailChanged` poke handler, which Unity emits
 * only after the new blob is written.
 */
export function invalidateShotFullImage(shotId: number): void {
  drop(shotId, true);
}

/**
 * Contract note 4, WEB side: the web just replaced (or cleared) this shot's image optimistically and
 * the write to Unity is still in flight. Evict, but do NOT re-ask — Unity's blob is stale BY
 * DEFINITION until that write lands, and a re-ask would beat it to the outbox and be answered with
 * the pixels we are replacing, flipping the viewer back to the old picture for the whole stale
 * window. The `thumbnailChanged` poke that follows Unity's ingest is what re-asks, via
 * invalidateShotFullImage above; meanwhile the consumer paints the web's own fresher copy, exactly
 * as the drop path's interim tile already does.
 */
export function dropShotFullImageForPendingWrite(shotId: number): void {
  drop(shotId, false);
}

/** Project switch: every shotId now means something else. Re-asks rather than merely clearing, so a
 *  viewer parked on the same shot id in the new project cannot keep the old project's frame — and
 *  Unity is already on the new document by the time it sends setSqlitePath. */
export function clearShotFullImageCache(): void {
  const ids = new Set<number>([...cache.keys(), ...waiters.keys()]);
  for (const id of ids) drop(id, true);
}

// ─── Inbound handler ─────────────────────────────────────────────────────

export const fullImageHandlers = {
  setShotFullImage: (msg: SetShotFullImageMessage) => {
    const { shotId, requestId, imageUrl } = msg.payload;
    // One filter covers four cases: a reply for a shot nobody asked about, a duplicate, the answer to
    // a request that invalidation already retired, and the answer to one that timed out.
    if (pending.get(shotId)?.requestId !== requestId) return;

    // Nothing to show. Waiters are told (so a spinner can stop / Task 9's promise can resolve) but
    // nothing is cached, leaving a later request free to try again.
    if (imageUrl === null || typeof imageUrl !== 'string' || imageUrl === '') {
      pending.delete(shotId);
      notify(shotId, null);
      return;
    }

    const decoding = startFullResDecode(imageUrl);
    if (decoding === null) { publish(shotId, requestId, imageUrl); return; }
    // `pending` stays CLAIMED across the decode, doing both of its jobs meanwhile: a second waiter
    // arriving now must not fire a duplicate request, and an invalidation now must be able to retire
    // this answer (publish re-checks). A decode that never settles leaves the shot claimed — the
    // same resting state as a reply that never comes, see note 1.
    decoding.then(
      () => publish(shotId, requestId, imageUrl),
      () => publish(shotId, requestId, imageUrl),
    );
  },
} satisfies HandlerMap;

/** Test-only reset — module state is process-wide, so suites would leak into each other. */
export function __resetFullImageForTests(): void {
  cache.clear();
  waiters.clear();
  pending.clear();
  requestSeq = 0;
}
